import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Badge, Button, Avatar, VStack, useTheme, useColorMode } from '@chakra-ui/react';
import { cqAPI } from '../../lib/Auth';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faComments } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from "../../AppContext";

function MonitoringChatDetail() {
  const { prevLocation } = useAppContext();
  const { chatId } = useParams();
  const [chatDetails, setChatDetails] = useState({
    sessionObj: {}
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const bgColor = colorMode === "light" ? theme.colors.lightBackground : theme.colors.darkBackground;
  const textColor = colorMode === "light" ? theme.colors.lightText : theme.colors.darkText;
  const secondaryTextColor = colorMode === "light" ? theme.colors.gray[600] : theme.colors.gray[400];
  const userMessageBg = colorMode === "light" ? theme.colors.blue[100] : theme.colors.blue[700];
  const assistantMessageBg = colorMode === "light" ? theme.colors.green[100] : theme.colors.green[700];

  const fetchChatDetails = async () => {
    try {
      const response = await cqAPI.get(`/monitoring/chat?id=${chatId}`);
      setChatDetails(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching chat details:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatDetails();
  }, [chatId]);

  if (loading) {
    return <Box>Loading...</Box>;
  }

  const formatDate = (date) => {
    return moment(date).format('LLL');
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleGoToConversation = () => {
    const uid = chatDetails.sessionObj?.uid;
    const bid = chatDetails.sessionObj?.bid;
    if (uid && bid) {
      navigate(`/inbox/${uid}?bid=${bid}`);
    }
  };

  return (
    <Flex direction="column" p="5" overflowY="auto" maxH="100vh">
      <Box className="chat-details" w="100%" textAlign="left">
        <Flex justifyContent="space-between" alignItems="center" mb="5">
          <Box>
            {prevLocation === '/monitoring/chats' && (
              <Button onClick={handleBackClick} variant="ghost">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to chat list
              </Button>
            )}
          </Box>
          <Box>
            <Button
              onClick={handleGoToConversation}
              colorScheme="blue"
              leftIcon={<FontAwesomeIcon icon={faComments} />}
            >
              Go to conversation
            </Button>
          </Box>
        </Flex>

        {!chatDetails?.sessionObj && "Could not find this chat session"}

        {chatDetails?.sessionObj && (
          <>
            <Flex wrap="wrap" mb={6}>
              <Box p="2" w="200px">
                <Text fontWeight="bold">Patient Name</Text>
                <Text>{chatDetails?.patient_name || 'N/A'}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Date & Time</Text>
                <Text>{formatDate(chatDetails?.sessionObj?.session_started_date)}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Message Count</Text>
                <Text>{chatDetails?.sessionObj?.message_count}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Business Name</Text>
                <Text>{chatDetails?.business_name || 'N/A'}</Text>
              </Box>

              <Box p="2" w="200px">
                <Text fontWeight="bold">Title</Text>
                <Text>{chatDetails?.sessionObj?.title || 'N/A'}</Text>
              </Box>

              {chatDetails?.sessionObj?.metadata && (
                <>
                  <Box p="2" w="200px">
                    <Text fontWeight="bold">Conversation Topics</Text>
                    {chatDetails.sessionObj.metadata.conversation_topics?.map((topic, i) => (
                      <li key={i}>{topic}</li>
                    ))}
                  </Box>

                  <Box p="2" w="200px">
                    <Text fontWeight="bold">Goals Scores</Text>
                    {chatDetails.sessionObj.metadata.goals_score?.map((score, i) => (
                      <Text key={i}>
                        {score}: {chatDetails.sessionObj.metadata.goals_score_reason?.[i] ?? '—'}
                      </Text>
                    ))}
                  </Box>

                  <Box p="2" w="200px">
                    <Text fontWeight="bold">Conversation Goals</Text>
                    {chatDetails.sessionObj.metadata.conversation_goals?.map((goal, i) => (
                      <li key={i}>{goal}</li>
                    ))}
                  </Box>

                  {chatDetails.sessionObj.metadata.ai_human_ratio && (
                    <Box p="2" w="200px">
                      <Text fontWeight="bold">AI/Human Ratio</Text>
                      <Text>{chatDetails.sessionObj.metadata.ai_human_ratio}</Text>
                    </Box>
                  )}
                </>
              )}
            </Flex>

            <Box mt="4">
              <Text fontSize="2xl" mb="4" fontWeight="bold">Chat History</Text>
              {chatDetails?.chat_history && chatDetails.chat_history.length > 0 ? (
                <VStack spacing={4} align="stretch">
                  {chatDetails.chat_history.map((message, index) => (
                    <Flex
                      key={index}
                      justifyContent={message.role === 'user' ? 'flex-start' : 'flex-end'}
                      w="100%"
                    >
                      <Flex
                        direction={message.role === 'user' ? 'row' : 'row-reverse'}
                        maxW="70%"
                        align="flex-start"
                      >
                        <Avatar
                          size="sm"
                          name={message.role === 'user' ? chatDetails?.patient_name || 'User' : 'AI'}
                          bg={message.role === 'user' ? theme.colors.blue[500] : theme.colors.green[500]}
                          color="white"
                          mr={message.role === 'user' ? 3 : 0}
                          ml={message.role === 'assistant' ? 3 : 0}
                        />
                        <Box
                          p={4}
                          borderRadius="lg"
                          bg={message.role === 'user' ? userMessageBg : assistantMessageBg}
                          boxShadow="sm"
                        >
                          <Text fontSize="xs" fontWeight="bold" mb={2} color={secondaryTextColor}>
                            {message.role === 'user' ? 'User' : 'Assistant'}
                          </Text>
                          <Text color={textColor}>{message.content}</Text>
                          {message.timestamp && (
                            <Text fontSize="xs" color={secondaryTextColor} mt={2}>
                              {moment(message.timestamp).format("MMM D, YYYY h:mm a")}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    </Flex>
                  ))}
                </VStack>
              ) : (
                <Text color={secondaryTextColor}>No chat history available</Text>
              )}
            </Box>
          </>
        )}
      </Box>
    </Flex>
  );
}

export default MonitoringChatDetail;